import { useMemo } from 'react';

import { expValEquals } from '@confluence/feature-experiments';

import type {
	UsePageIncludeMacroParamsProps,
	UsePageIncludeMacroParamsResult,
} from '../pageIncludeMacroTypes.ts';
import { parseMacroTarget } from '../util/pageIncludeMacroParams.ts';

type MacroAdfNode = {
	attrs?: {
		localId?: string;
		parameters?: {
			macroMetadata?: {
				macroId?: {
					value?: string;
				};
			};
		};
	};
};

/**
 * Processes macro props and params into structured values for the renderer component.
 * Handles macro parameter parsing only — GraphQL query target selection and fetching are separate.
 */
export const usePageIncludeMacroParams = ({
	macroDefaultProps,
	macroParams,
}: UsePageIncludeMacroParamsProps): UsePageIncludeMacroParamsResult => {
	const parsedTarget = useMemo(() => parseMacroTarget(macroParams), [macroParams]);
	const macroNode = macroDefaultProps.node as MacroAdfNode | undefined;
	// The macroMetadata/localId precedence is only needed to match the TOC
	// walker's heading-id prefix. Gate it so non-TOC renders keep the prior
	// macroId source.
	const macroId = expValEquals('confluence_toc_included_content_exp', 'isEnabled', true)
		? (macroNode?.attrs?.parameters?.macroMetadata?.macroId?.value ??
			macroNode?.attrs?.localId ??
			macroDefaultProps.attributes?.macroId ??
			undefined)
		: (macroDefaultProps.attributes?.macroId ?? undefined);
	const hostContentId: string = macroDefaultProps.contentId ?? '';

	return {
		parsedTarget,
		macroId: macroId ? `${macroId}` : undefined,
		hostContentId,
	};
};
